import React from 'react'
import { Button, Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap'
import { Clock as IconClock, CheckCircle as IconCheck } from 'react-feather'
import { Col, Row, Show } from '~/components/commons'

import { FormattedMessage, useIntl } from 'react-intl'
import { format } from 'date-fns'
import { ptBR, enUS } from 'date-fns/locale'
import _ from 'lodash'

import Spinner from '~/components/@vuexy/spinner/Loading-spinner'

const ContentLabel = ({ children }) => (
  <Col md={3} style={{ marginBottom: '20px' }} className="d-flex">
    <IconCheck size={24} color="#3d9244" />
    <label
      style={{
        color: '#000',
        fontFamily: 'Montserrat',
        marginLeft: '15px',
        fontSize: '16px',
        fontWeight: 500,
        paddingRight: '5px',
      }}
    >
      {children}
    </label>
  </Col>
)

const ContentInfo = ({ children }) => (
  <Col md={9} style={{ marginBottom: '20px' }}>
    <span style={{ color: '#606060', fontSize: '16px', fontWeight: 500 }}>
      {children}
    </span>
  </Col>
)

const OpenHours = ({ periods }) => (
  <Row style={{ marginBottom: '20px' }}>
    {periods
      ?.filter(p => p.openTime || p.closeTime)
      ?.map(p => (
        <>
          <Col md={4}>{_.capitalize(p.openDay)}</Col>
          <Col md={8}>{`${
            String(p.openTime.hours).padStart(2, '0') +
              ':' +
              String(p.openTime.minutes).padStart(2, '0') ?? 'Open at'
          } - ${
            String(p.closeTime.hours).padStart(2, '0') +
              ':' +
              String(p.closeTime.minutes).padStart(2, '0') ?? 'Close at'
          }`}</Col>
        </>
      ))}
  </Row>
)

const SpecialHours = ({ periods, locale }) => {
  const dateFormated = (dateObject, locale) => {
    try {
      const { year, month, day } = dateObject || {}
      const date = new Date(year, month, day)
      return format(date, 'P', {
        locale: locale === 'br' ? ptBR : enUS,
      })
    } catch (error) {
      return 'undefined'
    }
  }
  return (
    <Row style={{ marginBottom: '20px' }}>
      {periods
        ?.filter(p => p.openTime || p.closeTime)
        ?.map(p => (
          <>
            <Col md={4}>{dateFormated(p.startDate, locale)}</Col>
            <Col md={8}>{`${
              p.openTime.hours + ':' + p.openTime.minutes ?? 'Open at'
            } - ${
              p.closeTime.hours + ':' + p.closeTime.minutes ?? 'Close at'
            }`}</Col>
          </>
        ))}
    </Row>
  )
}

function PreviewGeneralInfo({
  modalData = {},
  toggleModal,
  isOpen,
  onSubmit,
  isSubmiting,
  isValid,
}) {
  const {
    title,
    categories,
    openInfo,
    websiteUri,
    phoneNumbers,
    adWordsLocationExtensions,
    labels,
    regularHours,
    specialHours,
    profile,
    scheduleEdition,
  } = modalData

  const intl = useIntl()

  function formatPhoneNumber(phoneNumberString) {
    var cleaned = ('' + phoneNumberString).replace(/\D/g, '')
    var match = cleaned.match(
      /^\s*(\d{2}|\d{0})(\d{2}|\d{0})[-. ]?(\d{5}|\d{4})[-. ]?(\d{4})[-. ]?\s*$/
    )
    if (match) {
      return ['(', match[2], ') ', match[3], '-', match[4]].join('')
    }
    return phoneNumberString
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggleModal} className="modal-lg">
        <ModalHeader toggle={toggleModal} style={{ paddingLeft: '25px' }}>
          <FormattedMessage id="Updated informations" />
        </ModalHeader>
        <ModalBody
          className="modal-dialog-centered"
          style={{ alignItems: 'stretch', padding: '25px' }}
        >
          <Row className="w-100 overflow-auto" style={{ maxHeight: '650px' }}>
            <Show condition={isSubmiting}>
              <Spinner />
            </Show>
            <Show condition={!!title}>
              <ContentLabel>
                <FormattedMessage id="Business name" />:
              </ContentLabel>
              <ContentInfo>{title}</ContentInfo>
            </Show>
            <Show condition={!!categories?.primaryCategory?.displayName}>
              <ContentLabel>
                <FormattedMessage id="Primary category" />:
              </ContentLabel>
              <ContentInfo>
                {categories?.primaryCategory.displayName}
              </ContentInfo>
            </Show>
            <Show condition={!!openInfo?.status}>
              <ContentLabel>
                <FormattedMessage id="Location status" />:
              </ContentLabel>
              <ContentInfo>
                {_.capitalize(openInfo?.status).replace('_', ' ')}
              </ContentInfo>
            </Show>
            <Show condition={!!websiteUri}>
              <ContentLabel>
                <FormattedMessage id="Website" />:
              </ContentLabel>
              <ContentInfo>{decodeURI(websiteUri)}</ContentInfo>
            </Show>
            <Show condition={regularHours?.periods?.length}>
              <ContentLabel>
                <FormattedMessage id="Open hours" />:
              </ContentLabel>
              <ContentInfo>
                <OpenHours periods={regularHours.periods} />
              </ContentInfo>
            </Show>
            <Show condition={specialHours?.specialHourPeriods?.length}>
              <ContentLabel>
                <FormattedMessage id="Special hours" />:
              </ContentLabel>
              <ContentInfo>
                <SpecialHours periods={specialHours.specialHourPeriods} />
              </ContentInfo>
            </Show>
            <Show condition={!!phoneNumbers?.primaryPhone}>
              <ContentLabel>
                <FormattedMessage id="Phone number" />:
              </ContentLabel>
              <ContentInfo>
                {formatPhoneNumber(phoneNumbers?.primaryPhone)}
              </ContentInfo>
            </Show>
            <Show condition={phoneNumbers?.additionalPhones?.length}>
              <ContentLabel>
                <FormattedMessage id="Additional phones" />:
              </ContentLabel>
              <ContentInfo>
                {phoneNumbers?.additionalPhones
                  .map(phone => formatPhoneNumber(phone))
                  .join(', ')}
              </ContentInfo>
            </Show>
            <Show condition={!!adWordsLocationExtensions?.adPhone}>
              <ContentLabel>
                <FormattedMessage id="Google Ads number" />:
              </ContentLabel>
              <ContentInfo>{adWordsLocationExtensions.adPhone}</ContentInfo>
            </Show>
            <Show condition={labels?.length}>
              <ContentLabel>
                <FormattedMessage id="Labels" />:
              </ContentLabel>
              <ContentInfo>{labels.join(', ')}</ContentInfo>
            </Show>
            <Show condition={!!profile.description}>
              <ContentLabel>
                <FormattedMessage id="Local description" />:
              </ContentLabel>
              <ContentInfo>{profile.description}</ContentInfo>
            </Show>
          </Row>
        </ModalBody>
        <ModalFooter
          className="d-flex justify-content-between"
          style={{ padding: '20px' }}
        >
          <div className="d-flex align-items-center">
            <IconClock size={33} className="mr-1" />
            <div>
              <h4 className="mb-0">
                <FormattedMessage id="Published at" />:
              </h4>
              <p className="mb-0">
                {!scheduleEdition ? (
                  <FormattedMessage id="Now" />
                ) : (
                  format(scheduleEdition, 'Pp', {
                    locale: intl.locale === 'br' ? ptBR : enUS,
                  })
                )}
              </p>
            </div>
          </div>
          <div className="d-flex">
            <Button
              outline
              color="primary"
              onClick={() => toggleModal(false)}
              disabled={isSubmiting}
            >
              <FormattedMessage id="Edit" />
            </Button>
            <Button
              color={scheduleEdition ? 'info' : 'success'}
              className="ml-2"
              onClick={onSubmit}
              disabled={isSubmiting || !isValid}
            >
              <FormattedMessage
                id={`Confirm & ${scheduleEdition ? 'Schedule' : 'Update'}`}
              />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default PreviewGeneralInfo
